import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import HeroBackground from './HeroBackground';
import StickyNote from './StickyNote';
import ScribbleUnderline from './ScribbleUnderline';

const TERMINAL_LINES = [
  '> Scanning inbox... 47 emails triaged',
  '> Calendar conflict resolved → Thursday 10AM',
  '> Meeting brief ready for Northgate Capital',
  '> 3 follow-ups drafted and queued',
];

const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 80, behavior: 'smooth' });
};

const TerminalTicker = () => {
  const [lineIdx, setLineIdx] = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  const [displayed, setDisplayed] = useState('');

  useEffect(() => {
    const line = TERMINAL_LINES[lineIdx];
    if (charIdx < line.length) {
      const t = setTimeout(() => { setDisplayed(line.slice(0, charIdx + 1)); setCharIdx(c => c + 1); }, 38);
      return () => clearTimeout(t);
    } else {
      const t = setTimeout(() => { setLineIdx(l => (l + 1) % TERMINAL_LINES.length); setCharIdx(0); setDisplayed(''); }, 2200);
      return () => clearTimeout(t);
    }
  }, [charIdx, lineIdx]);

  return (
    <div className="inline-flex items-center gap-3 px-4 py-2.5 bg-white border border-[#E4DFD8] rounded-lg shadow-sm font-mono-custom text-xs text-[#6B6560]">
      <span className="w-1.5 h-1.5 rounded-full bg-[#3D7A5E] animate-pulse-dot flex-shrink-0" />
      <span className="min-w-0 text-[#1A1A18]">{displayed}</span>
      <span className="text-[#2A5C8A] animate-pulse flex-shrink-0">|</span>
    </div>
  );
};

const Hero = () => (
  <div className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
    <HeroBackground />

    {/* Floating sticky notes — decorative */}
    <div className="absolute top-36 right-12 hidden xl:block animate-float-gentle" style={{ animationDelay: '0s' }}>
      <StickyNote color="yellow" rotate="3deg">
        <p className="text-xs font-semibold mb-1">📅 Thursday</p>
        <p className="text-xs">Sarah → 10AM ✓</p>
        <p className="text-xs">Deep work: protected</p>
        <p className="text-xs text-[#9E9690] mt-1">— moved by WeekOne</p>
      </StickyNote>
    </div>
    <div className="absolute top-64 left-8 hidden xl:block animate-float-gentle" style={{ animationDelay: '2s' }}>
      <StickyNote color="blue" rotate="-3deg">
        <p className="text-xs font-semibold mb-1">📬 Inbox cleared</p>
        <p className="text-xs">47 emails sorted</p>
        <p className="text-xs">3 drafts queued</p>
        <p className="text-xs text-[#1E4D7A] mt-1">✓ nothing missed</p>
      </StickyNote>
    </div>

    <div className="relative max-w-5xl mx-auto px-6 lg:px-8 z-10 flex flex-col items-center text-center py-20">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mb-8">
        <TerminalTicker />
      </motion.div>

      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4, delay: 0.1 }}
        className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#EBF2F9] border border-[#C8DDE9] mb-10">
        <span className="w-1.5 h-1.5 rounded-full bg-[#3D7A5E] animate-pulse-dot" />
        <span className="text-[11px] font-semibold text-[#2A5C8A] tracking-wide uppercase font-mono-custom">
          Accepting new clients — 48hr setup
        </span>
      </motion.div>

      <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
        className="text-5xl md:text-7xl font-extrabold tracking-tight text-[#1A1A18] max-w-4xl leading-[1.05] mb-3"
        style={{ letterSpacing: '-0.03em' }}>
        Stop managing.
        <br />
        <span className="gradient-text-accent">Start operating.</span>
      </motion.h1>

      {/* Scribble underline under accent */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="mb-6 w-64 mx-auto">
        <ScribbleUnderline color="rgba(42,92,138,0.3)" width={280} />
      </motion.div>

      <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25 }}
        className="text-lg md:text-xl text-[#6B6560] max-w-2xl leading-relaxed mb-4">
        An AI executive assistant that's live and handling your inbox, calendar, and meetings
        within 24 hours — and trained to sound like you by day seven.
      </motion.p>

      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.35 }}
        className="text-sm text-[#9E9690] mb-12">
        Built for founders, operators, and executives who move fast.
      </motion.p>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }}
        className="flex flex-col sm:flex-row gap-3 mb-20">
        <button className="btn-primary flex items-center justify-center gap-2 text-[15px] px-8 py-4">
          Book a 15-minute intro <SafeIcon icon={FiIcons.FiArrowRight} />
        </button>
        <button onClick={() => scrollToSection('how-it-works')} className="btn-secondary flex items-center justify-center gap-2 text-[15px]">
          <SafeIcon icon={FiIcons.FiPlay} className="text-[#2A5C8A]" />
          See it in action
        </button>
      </motion.div>

      {/* Stats row */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.55 }}
        className="w-full max-w-2xl bg-white border border-[#E4DFD8] rounded-2xl shadow-sm flex flex-wrap justify-center divide-x divide-[#EDE9E4]">
        {[
          { value: '24h', label: 'From kickoff to live', sub: 'avg. deployment time' },
          { value: '5–10h', label: 'Reclaimed per week', sub: 'avg. across clients' },
          { value: '$999', label: 'One-time setup', sub: 'vs. $5K+/mo human EA' },
        ].map((s) => (
          <div key={s.value} className="text-center px-6 py-4">
            <div className="text-2xl font-bold text-[#1A1A18] tracking-tight">{s.value}</div>
            <div className="text-xs text-[#6B6560] mt-0.5 leading-snug">{s.label}</div>
            <div className="text-[10px] font-mono-custom text-[#9E9690] mt-0.5">{s.sub}</div>
          </div>
        ))}
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.7 }}
        className="mt-12 flex flex-wrap justify-center gap-2">
        {[
          { icon: FiIcons.FiShield, text: 'Data stays yours' },
          { icon: FiIcons.FiZap, text: 'Live in 48 hours' },
          { icon: FiIcons.FiRefreshCw, text: 'No monthly SaaS fee' },
          { icon: FiIcons.FiCpu, text: 'Powered by OpenClaw' },
        ].map((item, i) => (
          <div key={i} className="tag-pill">
            <SafeIcon icon={item.icon} className="text-[#2A5C8A]" />
            {item.text}
          </div>
        ))}
      </motion.div>
    </div>
  </div>
);

export default Hero;